import React, { useState } from 'react'
import shortid from 'shortid'

import Button from '@react/react-spectrum/Button'
import { Table, THead, TBody, TH, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Select from '@react/react-spectrum/Select'

import Delete from '@react/react-spectrum/Icon/Delete'
import UsersExclude from '@react/react-spectrum/Icon/UsersExclude'
import ArrowUp from '@react/react-spectrum/Icon/ArrowUp'
import ArrowDown from '@react/react-spectrum/Icon/ArrowDown'

const EXTERNAL_SOURCE_ICON_ROOT = 'https://demo-system-next.s3.eu-north-1.amazonaws.com/logos'

const EmptyState = () => (
  <div className='pvs-emptyState'>
    <UsersExclude size={'L'} />
    <p>No properties in this section</p>
  </div>
)

const externalSources = [
  { label: '', value: '' },
  { label: 'Dynamics 365', value: 'dynamics' },
  { label: 'Google', value: 'google' },
  { label: 'Oracle', value: 'oracle' },
  { label: 'Salesforce', value: 'salesforce' },
  { label: 'SAP', value: 'sap' },
  { label: 'ServiceNow', value: 'servicenow' },
  { label: 'Snowflake', value: 'snowflake' },
  { label: 'Other', value: 'other' },
]

const addIds = (arr) => (Array.isArray(arr) ? arr.map((p) => ({ ...p, id: shortid() })) : [])

const Editor = ({ selectorLabel, selectorName, properties, disabled, onSave }) => {
  const [entries, setEntries] = useState(addIds(properties))
  const hasEntries = Array.isArray(entries) && entries.length > 0

  const addEntry = () => {
    setEntries((entries) => [...entries, { id: shortid() }])
  }

  const deleteEntry = (id) =>
    setEntries((entries) => {
      const newEntries = entries.filter((e) => e.id !== id)
      onSave(newEntries)
      return newEntries
    })

  const moveEntry = (id, direction) =>
    setEntries((entries) => {
      const index = entries.findIndex((e) => e.id === id)
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= entries.length) return entries
      const updated = [...entries]
      const [movedItem] = updated.splice(index, 1)
      updated.splice(newIndex, 0, movedItem)
      onSave(updated)
      return updated
    })

  const onFieldChange = (name, id) => (value) => {
    setEntries((entries) => {
      const newEntries = entries.map((e) => (e.id === id ? { ...e, [name]: value } : e))
      onSave(newEntries)
      return newEntries
    })
  }

  return (
    <div className='profile-viewer-section'>
      {hasEntries && (
        <Table className='pvs-table'>
          <THead>
            <TH width='250' className='pvs-label'>
              Label
            </TH>
            <TH className='pvs-selector'>{selectorLabel}</TH>
            <TH width='200' className='pvs-ext-source'>
              External Source
            </TH>
            <TH width='60' className='pvs-actions'>
              Actions
            </TH>
          </THead>
          <TBody>
            {entries.map((entry, index) => (
              <TR key={entry.id}>
                <TD>
                  <Textfield
                    name='label'
                    defaultValue={entry.label || ''}
                    placeholder='Property Label'
                    onChange={onFieldChange('label', entry.id)}
                    disabled={disabled}
                  />
                </TD>
                <TD>
                  <Textfield
                    name={selectorName}
                    defaultValue={entry[selectorName] || ''}
                    placeholder='Property Selector'
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    onChange={onFieldChange(selectorName, entry.id)}
                    disabled={disabled}
                  />
                </TD>

                <TD>
                  <Select
                    name='source'
                    value={entry.source || ''}
                    options={externalSources}
                    onChange={onFieldChange('source', entry.id)}
                    disabled={disabled}
                    renderItem={ExternalSourceItemRenderer}
                  />
                </TD>
                <TD>
                  <div className='pvs-row-actions'>
                    <Button
                      variant='tool'
                      aria-label='Move Up'
                      icon={<ArrowUp />}
                      onClick={() => moveEntry(entry.id, -1)}
                      disabled={disabled || index === 0}
                    />
                    <Button
                      variant='tool'
                      aria-label='Move Down'
                      icon={<ArrowDown />}
                      onClick={() => moveEntry(entry.id, 1)}
                      disabled={disabled || index === entries.length - 1}
                    />
                    <Button
                      variant='tool'
                      aria-label='Delete'
                      icon={<Delete />}
                      onClick={() => deleteEntry(entry.id)}
                      disabled={disabled}
                    />
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {!hasEntries && <EmptyState />}

      <Button label='Add Property' variant='primary' onClick={addEntry} disabled={disabled} />
    </div>
  )
}

function ExternalSourceItemRenderer(item) {
  return (
    <span className='pvs-external-sources-item'>
      {item.value && item.value !== 'other' && (
        <img src={`${EXTERNAL_SOURCE_ICON_ROOT}/${item.value}.svg`} alt={`${item.value} icon`} />
      )}
      {item.label}
    </span>
  )
}

export default Editor
